import React, { useState, useEffect } from 'react';

import { 
  Search, 
  Filter, 
  Download, 
  Eye, 
  Calendar,
  Users,
  DollarSign,
  ShoppingBag,
  TrendingUp,
  BarChart3,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  X,
  User,
  Building,
  Package,
  Truck,
  Store,
  FileText,
  MoreVertical,
  Printer,
  Share2,
  Activity,
  ArrowUpRight,
  ArrowDownRight,
  ShoppingCart,
  Percent,
  Tag,
  Scale,
  Package2
} from 'lucide-react';

const ViewActivity = () => {
  // State for sales activities
  const [salesActivities, setSalesActivities] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedItems, setSelectedItems] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [showSaleDetails, setShowSaleDetails] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'summary'
  
  const itemsPerPage = 10;
  
  // Sample activity data (mock)
  const sampleSales = [
    {
      id: 'ACT-2024-1001',
      date: '2024-12-15T09:30:00',
      itemName: 'Sugar for Union',
      category: 'sugar',
      unit: 'Kg',
      quantity: 1000,
      unitPrice: 85,
      totalPrice: 1000 * 85,
      recipientName: 'Union Members',
      activityLead: 'Logistics Team',
      location: 'Cooperative Warehouse',
      status: 'distributed',
      discount: 0,
      tax: 0,
      notes: 'Bulk sugar distribution for union members'
    },
    {
      id: 'ACT-2024-1002',
      date: '2024-12-15T10:15:00',
      itemName: 'Spaghetti Distribution',
      category: 'spaghetti',
      unit: 'Pack',
      quantity: 500,
      unitPrice: 95,
      totalPrice: 500 * 95,
      recipientName: 'Community Households',
      activityLead: 'Community Outreach',
      location: 'District Center',
      status: 'distributed',
      discount: 0,
      tax: 0,
      notes: 'Emergency food support'
    },
    {
      id: 'ACT-2024-1003',
      date: '2024-12-15T11:00:00',
      itemName: 'Rice Distribution',
      category: 'rice',
      unit: 'Kg',
      quantity: 800,
      unitPrice: 60,
      totalPrice: 800 * 60,
      recipientName: 'Vulnerable Families',
      activityLead: 'Relief Team',
      location: 'Warehouse B',
      status: 'distributed',
      discount: 0,
      tax: 0,
      notes: 'Rice provision program'
    },
    {
      id: 'ACT-2024-1004',
      date: '2024-12-15T14:30:00',
      itemName: 'Medical Camp',
      category: 'health',
      unit: 'Service',
      quantity: 1,
      unitPrice: 50000,
      totalPrice: 50000,
      recipientName: 'Community',
      activityLead: 'Health Dept',
      location: 'Community Hall',
      status: 'completed',
      discount: 0,
      tax: 0,
      notes: 'Free medical check-up'
    },
    {
      id: 'ACT-2024-1005',
      date: '2024-12-15T15:45:00',
      itemName: 'Seed Distribution',
      category: 'tea',
      unit: 'Kg',
      quantity: 40,
      unitPrice: 280,
      totalPrice: 11200,
      customerName: 'Café JKL',
      customerType: 'retail',
      paymentMethod: 'credit_card',
      salesPerson: 'Meron Tadesse',
      location: 'Beverage Section',
      status: 'completed',
      discount: 300,
      tax: 1120,
      notes: 'Black tea leaves'
    },
    {
      id: 'SALE-2024-1006',
      date: '2024-12-14T09:15:00',
      itemName: 'Wheat Flour',
      category: 'flour',
      unit: 'Kg',
      quantity: 200,
      unitPrice: 45,
      totalPrice: 9000,
      customerName: 'Bakery MNO',
      customerType: 'wholesale',
      paymentMethod: 'bank_transfer',
      salesPerson: 'Tesfaye Girma',
      location: 'Grains Section',
      status: 'completed',
      discount: 400,
      tax: 900,
      notes: 'All-purpose flour'
    },
    {
      id: 'SALE-2024-1007',
      date: '2024-12-14T10:30:00',
      itemName: 'Rice',
      category: 'rice',
      unit: 'Kg',
      quantity: 150,
      unitPrice: 60,
      totalPrice: 9000,
      customerName: 'Restaurant PQR',
      customerType: 'wholesale',
      paymentMethod: 'cash',
      salesPerson: 'John M. Doe',
      location: 'Grains Section',
      status: 'completed',
      discount: 300,
      tax: 900,
      notes: 'Long grain rice'
    },
    {
      id: 'SALE-2024-1008',
      date: '2024-12-14T13:45:00',
      itemName: 'Milk Powder',
      category: 'dairy',
      unit: 'Kg',
      quantity: 80,
      unitPrice: 180,
      totalPrice: 14400,
      customerName: 'Café STU',
      customerType: 'retail',
      paymentMethod: 'mobile_money',
      salesPerson: 'Sarah Johnson',
      location: 'Dairy Section',
      status: 'completed',
      discount: 600,
      tax: 1440,
      notes: 'Full cream milk powder'
    },
    {
      id: 'SALE-2024-1009',
      date: '2024-12-14T16:00:00',
      itemName: 'Salt',
      category: 'salt',
      unit: 'Kg',
      quantity: 120,
      unitPrice: 25,
      totalPrice: 3000,
      customerName: 'Various Customers',
      customerType: 'retail',
      paymentMethod: 'cash',
      salesPerson: 'Michael Bekele',
      location: 'Spices Section',
      status: 'completed',
      discount: 0,
      tax: 300,
      notes: 'Iodized salt retail sales'
    },
    {
      id: 'SALE-2024-1010',
      date: '2024-12-13T08:45:00',
      itemName: 'Pasta',
      category: 'pasta',
      unit: 'Kg',
      quantity: 90,
      unitPrice: 95,
      totalPrice: 8550,
      customerName: 'Italian Restaurant',
      customerType: 'wholesale',
      paymentMethod: 'bank_transfer',
      salesPerson: 'Abebe Kebede',
      location: 'Processed Foods',
      status: 'completed',
      discount: 350,
      tax: 855,
      notes: 'Spaghetti pasta'
    },
    {
      id: 'SALE-2024-1011',
      date: '2024-12-13T11:20:00',
      itemName: 'Chicken Meat',
      category: 'poultry',
      unit: 'Kg',
      quantity: 35,
      unitPrice: 320,
      totalPrice: 11200,
      customerName: 'Fast Food Chain',
      customerType: 'wholesale',
      paymentMethod: 'credit_card',
      salesPerson: 'Meron Tadesse',
      location: 'Butchery Section',
      status: 'completed',
      discount: 500,
      tax: 1120,
      notes: 'Fresh chicken parts'
    },
    {
      id: 'SALE-2024-1012',
      date: '2024-12-13T14:10:00',
      itemName: 'Honey',
      category: 'honey',
      unit: 'Kg',
      quantity: 20,
      unitPrice: 400,
      totalPrice: 8000,
      customerName: 'Health Store',
      customerType: 'retail',
      paymentMethod: 'cash',
      salesPerson: 'Tesfaye Girma',
      location: 'Natural Products',
      status: 'completed',
      discount: 200,
      tax: 800,
      notes: 'Pure natural honey'
    },
    {
      id: 'SALE-2024-1013',
      date: '2024-12-12T10:00:00',
      itemName: 'Butter',
      category: 'dairy',
      unit: 'Kg',
      quantity: 25,
      unitPrice: 280,
      totalPrice: 7000,
      customerName: 'Bakery DEF',
      customerType: 'wholesale',
      paymentMethod: 'bank_transfer',
      salesPerson: 'John M. Doe',
      location: 'Dairy Section',
      status: 'completed',
      discount: 300,
      tax: 700,
      notes: 'Salted butter'
    },
    {
      id: 'SALE-2024-1014',
      date: '2024-12-12T12:30:00',
      itemName: 'Lentils',
      category: 'pulses',
      unit: 'Kg',
      quantity: 180,
      unitPrice: 120,
      totalPrice: 21600,
      customerName: 'Various Customers',
      customerType: 'retail',
      paymentMethod: 'cash',
      salesPerson: 'Sarah Johnson',
      location: 'Pulses Section',
      status: 'completed',
      discount: 800,
      tax: 2160,
      notes: 'Mixed retail sales'
    },
    {
      id: 'SALE-2024-1015',
      date: '2024-12-12T15:45:00',
      itemName: 'Spices Mix',
      category: 'spices',
      unit: 'Kg',
      quantity: 45,
      unitPrice: 350,
      totalPrice: 15750,
      customerName: 'Restaurant ABC',
      customerType: 'wholesale',
      paymentMethod: 'cash',
      salesPerson: 'Michael Bekele',
      location: 'Spices Section',
      status: 'completed',
      discount: 750,
      tax: 1575,
      notes: 'Special restaurant blend'
    }
  ];

  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      setSalesActivities(sampleSales);
      setLoading(false);
    }, 1000);
  }, []);

  // Product categories
  const categories = [
    { value: 'oil', label: 'Edible Oil', color: 'bg-yellow-100 text-yellow-800', icon: Package },
    { value: 'meat', label: 'Meat', color: 'bg-red-100 text-red-800', icon: Package },
    { value: 'sugar', label: 'Sugar', color: 'bg-white text-gray-800', icon: Package2 },
    { value: 'coffee', label: 'Coffee', color: 'bg-brown-100 text-brown-800', icon: Package },
    { value: 'tea', label: 'Tea', color: 'bg-green-100 text-green-800', icon: Package },
    { value: 'flour', label: 'Flour', color: 'bg-white text-gray-800', icon: Scale },
    { value: 'rice', label: 'Rice', color: 'bg-gray-100 text-gray-800', icon: Package },
    { value: 'dairy', label: 'Dairy', color: 'bg-blue-100 text-blue-800', icon: Package },
    { value: 'salt', label: 'Salt', color: 'bg-gray-100 text-gray-800', icon: Package },
    { value: 'pasta', label: 'Pasta', color: 'bg-orange-100 text-orange-800', icon: Package },
    { value: 'poultry', label: 'Poultry', color: 'bg-pink-100 text-pink-800', icon: Package },
    { value: 'honey', label: 'Honey', color: 'bg-amber-100 text-amber-800', icon: Package },
    { value: 'pulses', label: 'Pulses', color: 'bg-purple-100 text-purple-800', icon: Package },
    { value: 'spices', label: 'Spices', color: 'bg-red-100 text-red-800', icon: Package }
  ];

  // Payment methods
  const paymentMethods = [
    { value: 'cash', label: 'Cash', color: 'bg-green-100 text-green-800' },
    { value: 'bank_transfer', label: 'Bank Transfer', color: 'bg-blue-100 text-blue-800' },
    { value: 'mobile_money', label: 'Mobile Money', color: 'bg-purple-100 text-purple-800' },
    { value: 'credit_card', label: 'Credit Card', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'check', label: 'Check', color: 'bg-gray-100 text-gray-800' }
  ];

  // Customer types
  const customerTypes = [
    { value: 'wholesale', label: 'Wholesale', color: 'bg-blue-100 text-blue-800' },
    { value: 'retail', label: 'Retail', color: 'bg-green-100 text-green-800' },
    { value: 'member', label: 'Member', color: 'bg-purple-100 text-purple-800' },
    { value: 'guest', label: 'Guest', color: 'bg-gray-100 text-gray-800' }
  ];

  // Status options
  const statusOptions = [
    { value: 'completed', label: 'Completed', color: 'bg-green-100 text-green-800', icon: CheckCircle },
    { value: 'pending', label: 'Pending', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
    { value: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-800', icon: XCircle },
    { value: 'refunded', label: 'Refunded', color: 'bg-gray-100 text-gray-800', icon: XCircle }
  ];

  // Filter and sort sales activities
  const filteredSales = salesActivities.filter(sale => {
    const customerOrRecipient = (sale.customerName || sale.recipientName || '').toString();
    const salesPersonOrLead = (sale.salesPerson || sale.activityLead || '').toString();
    const matchesSearch = 
      (sale.id || '').toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
      (sale.itemName || '').toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
      customerOrRecipient.toLowerCase().includes(searchTerm.toLowerCase()) ||
      salesPersonOrLead.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = selectedCategory === 'all' || sale.category === selectedCategory;
    
    // Filter by selected date
    const saleDate = new Date(sale.date).toISOString().split('T')[0];
    const matchesDate = selectedDate === '' || saleDate === selectedDate;
    
    return matchesSearch && matchesCategory && matchesDate;
  }).sort((a, b) => {
    if (sortBy === 'date') {
      return sortOrder === 'desc'
        ? new Date(b.date) - new Date(a.date)
        : new Date(a.date) - new Date(b.date);
    } else if (sortBy === 'totalPrice') {
      return sortOrder === 'desc'
        ? b.totalPrice - a.totalPrice
        : a.totalPrice - b.totalPrice;
    } else if (sortBy === 'quantity') {
      return sortOrder === 'desc'
        ? b.quantity - a.quantity
        : a.quantity - b.quantity;
    } else if (sortBy === 'itemName') {
      return sortOrder === 'desc'
        ? b.itemName.localeCompare(a.itemName)
        : a.itemName.localeCompare(b.itemName);
    }
    return 0;
  });

  // Pagination
  const totalPages = Math.ceil(filteredSales.length / itemsPerPage);
  const paginatedSales = filteredSales.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Formatting functions
  const formatCurrency = (amount) => {
    try {
      const formatted = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount);
      return `${formatted} BIRR`;
    } catch (e) {
      return amount + ' BIRR';
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }) + ' ' + date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDateOnly = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Selection handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedItems(paginatedSales.map(s => s.id));
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectItem = (itemId) => {
    setSelectedItems(prev => 
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  // Sale details handler
  const handleViewSale = (sale) => {
    setSelectedSale(sale);
    setShowSaleDetails(true);
  };

  // Calculate statistics
  const calculateStats = () => {
    const totalSales = filteredSales.length;
    const totalRevenue = filteredSales.reduce((sum, sale) => sum + sale.totalPrice, 0);
    const totalQuantity = filteredSales.reduce((sum, sale) => sum + sale.quantity, 0);
    const totalDiscount = filteredSales.reduce((sum, sale) => sum + sale.discount, 0);
    const totalTax = filteredSales.reduce((sum, sale) => sum + sale.tax, 0);
    
    // Today's sales
    const today = new Date().toDateString();
    const todaySales = filteredSales.filter(sale => 
      new Date(sale.date).toDateString() === today
    );
    const todayRevenue = todaySales.reduce((sum, sale) => sum + sale.totalPrice, 0);
    
    // Category-wise sales
    const categorySales = {};
    filteredSales.forEach(sale => {
      if (!categorySales[sale.category]) {
        categorySales[sale.category] = { revenue: 0, quantity: 0 };
      }
      categorySales[sale.category].revenue += sale.totalPrice;
      categorySales[sale.category].quantity += sale.quantity;
    });
    
    // Top selling items
    const itemSales = {};
    filteredSales.forEach(sale => {
      if (!itemSales[sale.itemName]) {
        itemSales[sale.itemName] = { revenue: 0, quantity: 0 };
      }
      itemSales[sale.itemName].revenue += sale.totalPrice;
      itemSales[sale.itemName].quantity += sale.quantity;
    });
    
    // Sort items by revenue
    const topItems = Object.entries(itemSales)
      .map(([name, data]) => ({ name, ...data }))
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 5);
    
    return { 
      totalSales, 
      totalRevenue, 
      totalQuantity, 
      totalDiscount,
      totalTax,
      todaySales: todaySales.length,
      todayRevenue,
      categorySales,
      topItems,
      netRevenue: totalRevenue - totalDiscount
    };
  };

  const stats = calculateStats();

  // UI helper functions
  const getCategoryColor = (category) => {
    const categoryInfo = categories.find(c => c.value === category);
    return categoryInfo ? categoryInfo.color : 'bg-gray-100 text-gray-800';
  };

  const getPaymentMethodColor = (method) => {
    const methodInfo = paymentMethods.find(m => m.value === method);
    return methodInfo ? methodInfo.color : 'bg-gray-100 text-gray-800';
  };

  const getCustomerTypeColor = (type) => {
    const typeInfo = customerTypes.find(t => t.value === type);
    return typeInfo ? typeInfo.color : 'bg-gray-100 text-gray-800';
  };

  const getCategoryLabel = (category) => {
    const categoryInfo = categories.find(c => c.value === category);
    return categoryInfo ? categoryInfo.label : category;
  };

  // Export functionality
  const handleExportCSV = () => {
    const headers = ['ID', 'Date', 'Item Name', 'Category', 'Unit', 'Quantity', 'Unit Price', 'Total Price', 'Customer', 'Payment Method', 'Sales Person'];
    const csvContent = [
      headers.join(','),
      ...filteredSales.map(sale => [
        sale.id,
        sale.date,
        sale.itemName,
        sale.category,
        sale.unit,
        sale.quantity,
        sale.unitPrice,
        sale.totalPrice,
        sale.customerName,
        sale.paymentMethod,
        sale.salesPerson
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cooperative_sales_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  // Handle date change
  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      
      <div className="flex-1 p-4 md:p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-r from-green-600 to-blue-600 flex items-center justify-center">
                  <ShoppingCart className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Cooperative Activities</h1>
                  <p className="text-gray-600">Track and manage daily cooperative activities and distributions</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 px-4 py-2 bg-green-50 rounded-lg">
                  <DollarSign className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-medium text-green-700">
                    {formatCurrency(stats.totalRevenue)} Total Cost (BIRR)
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setViewMode('table')}
                    className={`px-4 py-2 rounded-lg ${viewMode === 'table' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
                  >
                    Table View
                  </button>
                  <button
                    onClick={() => setViewMode('summary')}
                    className={`px-4 py-2 rounded-lg ${viewMode === 'summary' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
                  >
                    Summary View
                  </button>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white rounded-xl shadow border border-gray-200 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total Activities</p>
                    <p className="text-2xl font-bold text-gray-800">{stats.totalSales}</p>
                    <p className="text-xs text-green-600 mt-1">
                      {stats.todaySales} activities today
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                    <ShoppingCart className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow border border-gray-200 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total Cost (BIRR)</p>
                    <p className="text-2xl font-bold text-gray-800">
                      {formatCurrency(stats.totalRevenue)}
                    </p>
                    <p className="text-xs text-blue-600 mt-1">
                      {formatCurrency(stats.todayRevenue)} today
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow border border-gray-200 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total Quantity Distributed</p>
                    <p className="text-2xl font-bold text-gray-800">{stats.totalQuantity}</p>
                    <div className="flex items-center mt-1">
                      <Package className="w-4 h-4 text-purple-500" />
                      <span className="text-xs text-purple-600 ml-1">
                        {filteredSales.length > 0 ? (stats.totalQuantity / filteredSales.length).toFixed(1) : 0} avg/unit
                      </span>
                    </div>
                  </div>
                  <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
                    <Package className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow border border-gray-200 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Net Cost</p>
                    <p className="text-2xl font-bold text-gray-800">
                      {formatCurrency(stats.netRevenue)}
                    </p>
                    <div className="flex items-center mt-1">
                      <ArrowUpRight className="w-4 h-4 text-green-500" />
                      <span className="text-xs text-green-600 ml-1">
                        Discount: {formatCurrency(stats.totalDiscount)}
                      </span>
                    </div>
                  </div>
                  <div className="w-12 h-12 rounded-lg bg-yellow-100 flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-yellow-600" />
                  </div>
                </div>
              </div>
            </div>

            {/* Category-wise Activity Summary */}
            {viewMode === 'summary' && (
              <div className="bg-white rounded-xl shadow border border-gray-200 p-4 mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Category-wise Activity Summary</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {Object.entries(stats.categorySales).map(([category, data]) => {
                    const categoryInfo = categories.find(c => c.value === category);
                    const percentage = stats.totalRevenue > 0 ? (data.revenue / stats.totalRevenue) * 100 : 0;
                    
                    return (
                      <div key={category} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className={`w-8 h-8 rounded-lg ${categoryInfo?.color || 'bg-gray-100'} flex items-center justify-center`}>
                              <Package className="w-4 h-4" />
                            </div>
                            <span className="font-medium text-gray-800">
                              {categoryInfo?.label || category}
                            </span>
                          </div>
                          <span className="text-sm text-gray-500">{percentage.toFixed(1)}%</span>
                        </div>
                        <div className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Revenue:</span>
                            <span className="font-medium text-green-600">
                              {formatCurrency(data.revenue)}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Quantity:</span>
                            <span className="font-medium text-blue-600">
                              {data.quantity} units
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Avg Price:</span>
                            <span className="font-medium text-purple-600">
                              {data.quantity > 0 ? formatCurrency(data.revenue / data.quantity) : formatCurrency(0)}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Top Selling Items */}
            {viewMode === 'summary' && stats.topItems.length > 0 && (
              <div className="bg-white rounded-xl shadow border border-gray-200 p-4 mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Top Activities / Items</h3>
                <div className="space-y-3">
                  {stats.topItems.map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                          <span className="font-bold text-blue-600">{index + 1}</span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-800">{item.name}</p>
                          <p className="text-sm text-gray-600">{item.quantity} units sold</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-green-600">{formatCurrency(item.revenue)}</p>
                        <p className="text-sm text-gray-600">
                          Avg: {formatCurrency(item.quantity > 0 ? item.revenue / item.quantity : 0)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Search and Filters Bar */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 mb-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search activities by item name, recipient, lead, or ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  <Filter className="w-4 h-4" />
                  Filters
                  {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                
                <button
                  onClick={handleExportCSV}
                  className="flex items-center gap-2 px-4 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100"
                >
                  <Download className="w-4 h-4" />
                  Export CSV
                </button>
              </div>
            </div>

            {/* Advanced Filters */}
            {showFilters && (
              <div className="border-t border-gray-200 pt-4 mt-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Product Category
                    </label>
                    <select
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="all">All Categories</option>
                      {categories.map(category => (
                        <option key={category.value} value={category.value}>
                          {category.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date Filter
                    </label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="date"
                        value={selectedDate}
                        onChange={handleDateChange}
                        className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sort By
                    </label>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="date">Date (Newest)</option>
                      <option value="totalPrice">Total Price (High to Low)</option>
                      <option value="quantity">Quantity (High to Low)</option>
                      <option value="itemName">Item Name (A-Z)</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sort Order
                    </label>
                    <select
                      value={sortOrder}
                      onChange={(e) => setSortOrder(e.target.value)}
                      className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="desc">Descending</option>
                      <option value="asc">Ascending</option>
                    </select>
                  </div>
                </div>
                
                <div className="flex justify-end gap-3 mt-4">
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setSelectedCategory('all');
                      setSelectedDate(new Date().toISOString().split('T')[0]);
                      setSortBy('date');
                      setSortOrder('desc');
                    }}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 flex items-center gap-2"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Reset Filters
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Table View */}
          {viewMode === 'table' && (
            <>
              {/* Action Bar */}
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={selectedItems.length === paginatedSales.length && paginatedSales.length > 0}
                      onChange={handleSelectAll}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="text-sm text-gray-600">
                      {selectedItems.length > 0 
                        ? `${selectedItems.length} selected` 
                        : 'Select all'}
                    </span>
                  </div>
                </div>
                
                <div className="text-sm text-gray-600">
                  Showing {filteredSales.length} of {salesActivities.length} sales records
                </div>
              </div>

              {/* Sales Table */}
              {loading ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading sales data...</p>
                </div>
              ) : (
                <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            <input
                              type="checkbox"
                              checked={selectedItems.length === paginatedSales.length && paginatedSales.length > 0}
                              onChange={handleSelectAll}
                              className="w-4 h-4 text-blue-600 rounded"
                            />
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Sale ID & Date
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Item Details
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Quantity & Unit
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Price Information
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Customer Details
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {paginatedSales.map(sale => (
                          <tr key={sale.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <input
                                type="checkbox"
                                checked={selectedItems.includes(sale.id)}
                                onChange={() => handleSelectItem(sale.id)}
                                className="w-4 h-4 text-blue-600 rounded"
                              />
                            </td>
                            <td className="px-6 py-4">
                              <span className="font-mono text-sm font-medium text-blue-600">
                                {sale.id}
                              </span>
                              <div className="text-xs text-gray-500 mt-1">
                                {formatDateTime(sale.date)}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-lg ${getCategoryColor(sale.category)} flex items-center justify-center`}>
                                  <Package className="w-5 h-5" />
                                </div>
                                <div>
                                  <h4 className="font-medium text-gray-900">{sale.itemName}</h4>
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(sale.category)}`}>
                                    {getCategoryLabel(sale.category)}
                                  </span>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-center">
                                <div className="text-lg font-bold text-blue-600">
                                  {sale.quantity}
                                </div>
                                <div className="text-sm text-gray-600">{sale.unit}</div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="space-y-1">
                                <div className="flex justify-between text-sm">
                                  <span className="text-gray-600">Unit Price:</span>
                                  <span className="font-medium">{formatCurrency(sale.unitPrice)}</span>
                                </div>
                                {sale.discount > 0 && (
                                  <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">Discount:</span>
                                    <span className="font-medium text-red-600">-{formatCurrency(sale.discount)}</span>
                                  </div>
                                )}
                                {sale.tax > 0 && (
                                  <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">Tax:</span>
                                    <span className="font-medium text-purple-600">+{formatCurrency(sale.tax)}</span>
                                  </div>
                                )}
                                <div className="flex justify-between text-sm font-bold pt-1 border-t">
                                  <span className="text-gray-800">Total Price:</span>
                                  <span className="text-green-600">{formatCurrency(sale.totalPrice)}</span>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="space-y-2">
                                <div>
                                  <p className="font-medium text-gray-900">{sale.customerName}</p>
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCustomerTypeColor(sale.customerType)}`}>
                                    {customerTypes.find(t => t.value === sale.customerType)?.label}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPaymentMethodColor(sale.paymentMethod)}`}>
                                    {paymentMethods.find(m => m.value === sale.paymentMethod)?.label}
                                  </span>
                                </div>
                                <div className="text-xs text-gray-500">
                                  Sold by: {sale.salesPerson}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => handleViewSale(sale)}
                                  className="p-2 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg"
                                  title="View Details"
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                                <button
                                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg"
                                  title="Print Receipt"
                                >
                                  <Printer className="w-4 h-4" />
                                </button>
                                <button
                                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg"
                                  title="More Options"
                                >
                                  <MoreVertical className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Pagination */}
              {filteredSales.length > 0 && (
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mt-8">
                  <div className="text-sm text-gray-600">
                    Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, filteredSales.length)} of {filteredSales.length} sales
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`px-3 py-2 rounded-lg ${currentPage === pageNum ? 'bg-blue-600 text-white' : 'bg-white border border-gray-300 hover:bg-gray-50'}`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                  
                  <div className="text-sm text-gray-600">
                    Page {currentPage} of {totalPages}
                  </div>
                </div>
              )}

              {/* Empty State */}
              {filteredSales.length === 0 && !loading && (
                <div className="text-center py-12">
                  <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-6">
                    <ShoppingCart className="w-10 h-10 text-gray-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">No sales records found</h3>
                  <p className="text-gray-600 mb-6">
                    {searchTerm || selectedCategory !== 'all' || selectedDate !== ''
                      ? 'Try adjusting your search or filters'
                      : 'No sales have been recorded yet'}
                  </p>
                  <button 
                    onClick={() => {
                      setSearchTerm('');
                      setSelectedCategory('all');
                      setSelectedDate(new Date().toISOString().split('T')[0]);
                    }}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                  >
                    <RefreshCw className="w-5 h-5 inline mr-2" />
                    Reset Filters
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Sale Details Modal */}
      {showSaleDetails && selectedSale && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">Sale Details</h2>
                  <p className="text-gray-600">{selectedSale.id}</p>
                </div>
                <button
                  onClick={() => setShowSaleDetails(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Item Details */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-lg ${getCategoryColor(selectedSale.category)} flex items-center justify-center`}>
                        <Package className="w-6 h-6" />
                      </div>
                      <div>
                        <h3 className="font-bold text-lg text-gray-800">{selectedSale.itemName}</h3>
                        <p className="text-gray-600">{getCategoryLabel(selectedSale.category)}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-green-600">{formatCurrency(selectedSale.totalPrice)}</p>
                      <p className="text-sm text-gray-600">Total Amount</p>
                    </div>
                  </div>
                </div>

                {/* Price Breakdown */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-3">Price Breakdown</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Quantity:</span>
                      <span className="font-medium">{selectedSale.quantity} {selectedSale.unit}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Unit Price:</span>
                      <span className="font-medium">{formatCurrency(selectedSale.unitPrice)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Subtotal:</span>
                      <span className="font-medium">{formatCurrency(selectedSale.quantity * selectedSale.unitPrice)}</span>
                    </div>
                    {selectedSale.discount > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Discount:</span>
                        <span className="font-medium text-red-600">-{formatCurrency(selectedSale.discount)}</span>
                      </div>
                    )}
                    {selectedSale.tax > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Tax:</span>
                        <span className="font-medium text-purple-600">+{formatCurrency(selectedSale.tax)}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center pt-2 border-t">
                      <span className="text-lg font-bold text-gray-800">Total Price:</span>
                      <span className="text-lg font-bold text-green-600">{formatCurrency(selectedSale.totalPrice)}</span>
                    </div>
                  </div>
                </div>

                {/* Customer Details */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-3">Customer Details</h4>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Customer Name:</span>
                        <span className="font-medium">{selectedSale.customerName}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Customer Type:</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCustomerTypeColor(selectedSale.customerType)}`}>
                          {customerTypes.find(t => t.value === selectedSale.customerType)?.label}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Payment Method:</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPaymentMethodColor(selectedSale.paymentMethod)}`}>
                          {paymentMethods.find(m => m.value === selectedSale.paymentMethod)?.label}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Transaction Info */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-3">Transaction Information</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Date & Time</p>
                      <p className="font-medium">{formatDateTime(selectedSale.date)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Sales Person</p>
                      <p className="font-medium">{selectedSale.salesPerson}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Location</p>
                      <p className="font-medium">{selectedSale.location}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Status</p>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(selectedSale.status)}`}>
                        {selectedSale.status}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Notes */}
                {selectedSale.notes && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-2">Notes</h4>
                    <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                      <p className="text-gray-700">{selectedSale.notes}</p>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-6 border-t">
                  <button
                    onClick={() => {
                      window.print();
                    }}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 flex items-center gap-2"
                  >
                    <Printer className="w-4 h-4" />
                    Print Receipt
                  </button>
                  <button
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    onClick={() => setShowSaleDetails(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewActivity;